import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  TrendingUp, TrendingDown, Wallet, Activity, ArrowUpRight,
  MapPin, BedDouble, Bath, Star, Search, Home, Heart, ShoppingBag, User,
} from 'lucide-react';

/* ---------------- Desktop: Fintech dashboard (dark mode) ---------------- */
const FintechScreen = () => (
  <div className="w-full h-full bg-[#0E1116] text-white flex text-[7px] leading-tight select-none">
    {/* Sidebar */}
    <div className="w-[16%] bg-[#141922] flex flex-col items-center py-3 gap-3">
      <div className="w-3 h-3 rounded-md bg-[#C8102E]" />
      {[Activity, Wallet, TrendingUp].map((Icon, i) => (
        <div key={i} className={`w-4 h-4 rounded-md flex items-center justify-center ${i === 0 ? 'bg-white/10' : ''}`}>
          <Icon size={9} className="text-gray-400" />
        </div>
      ))}
    </div>
    {/* Main */}
    <div className="flex-1 p-3">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-gray-500 text-[6px]">Total Balance</p>
          <p className="text-base font-bold">$248,910.20</p>
        </div>
        <div className="flex items-center gap-1 bg-emerald-500/15 text-emerald-400 rounded-full px-2 py-0.5">
          <TrendingUp size={8} /> <span className="text-[6px]">+12.4%</span>
        </div>
      </div>
      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[
          { label: 'Revenue', val: '$92.4k', up: true, c: 'text-emerald-400' },
          { label: 'Expenses', val: '$41.2k', up: false, c: 'text-rose-400' },
          { label: 'Profit', val: '$51.2k', up: true, c: 'text-emerald-400' },
        ].map((s) => (
          <div key={s.label} className="bg-[#181E29] rounded-lg p-2">
            <p className="text-gray-500 text-[6px]">{s.label}</p>
            <p className="font-bold text-[9px]">{s.val}</p>
            <div className={`flex items-center gap-0.5 ${s.c}`}>
              {s.up ? <TrendingUp size={7} /> : <TrendingDown size={7} />}
              <span className="text-[6px]">{s.up ? '8.1' : '3.2'}%</span>
            </div>
          </div>
        ))}
      </div>
      {/* Chart */}
      <div className="bg-[#181E29] rounded-lg p-2 mb-2">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[6px] text-gray-400">Portfolio Performance</p>
          <p className="text-[6px] text-[#C8102E]">1Y</p>
        </div>
        <svg viewBox="0 0 200 60" className="w-full h-[46px]">
          <defs>
            <linearGradient id="fillg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C8102E" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#C8102E" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,45 C20,40 30,20 50,25 C70,30 80,10 100,15 C120,20 140,8 160,12 C175,15 190,5 200,8 L200,60 L0,60 Z" fill="url(#fillg)" />
          <path d="M0,45 C20,40 30,20 50,25 C70,30 80,10 100,15 C120,20 140,8 160,12 C175,15 190,5 200,8" fill="none" stroke="#C8102E" strokeWidth="1.5" />
        </svg>
      </div>
      {/* Bars */}
      <div className="flex items-end gap-1 h-6">
        {[40, 65, 50, 80, 55, 90, 70, 60].map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-white/10" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  </div>
);

/* ---------------- Tablet: Real-estate booking ---------------- */
const RealEstateScreen = () => (
  <div className="w-full h-full bg-white flex flex-col text-[7px] select-none">
    <div className="relative h-[58%]">
      <img
        src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=700&q=80&auto=format&fit=crop"
        alt="Luxury property"
        className="w-full h-full object-cover"
      />
      <div className="absolute top-2 left-2 bg-white/90 backdrop-blur rounded-full px-2 py-0.5 text-[6px] font-semibold text-[#1A1A1A]">
        Featured
      </div>
      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white/90 flex items-center justify-center">
        <Heart size={9} className="text-[#C8102E]" />
      </div>
      <div className="absolute bottom-2 left-2 text-white">
        <p className="text-[10px] font-bold drop-shadow">The Marina Residence</p>
        <p className="flex items-center gap-0.5 text-[6px] drop-shadow"><MapPin size={7} /> Marina Bay, Singapore</p>
      </div>
    </div>
    <div className="flex-1 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-base font-bold text-[#1A1A1A]">$4,200<span className="text-[7px] text-gray-400 font-normal">/mo</span></p>
        <div className="flex items-center gap-0.5 text-amber-500"><Star size={9} fill="currentColor" /> <span className="text-[#1A1A1A] font-semibold">4.9</span></div>
      </div>
      <div className="flex gap-3 text-gray-500 mb-3">
        <span className="flex items-center gap-1"><BedDouble size={9} /> 3 Beds</span>
        <span className="flex items-center gap-1"><Bath size={9} /> 2 Baths</span>
        <span className="flex items-center gap-1"><Home size={9} /> 1,450 sqft</span>
      </div>
      <div className="flex gap-2">
        {['photo-1560448204-e02f11c3d0e2', 'photo-1560185007-cde436f6a4d0', 'photo-1560184897-ae75f418493e'].map((id) => (
          <img key={id} src={`https://images.unsplash.com/${id}?w=200&q=70&auto=format&fit=crop`} alt="interior" className="w-1/3 h-8 object-cover rounded-md" />
        ))}
      </div>
      <div className="mt-3 bg-[#1A1A1A] text-white rounded-lg py-2 text-center font-semibold text-[8px]">
        Book a Viewing
      </div>
    </div>
  </div>
);

/* ---------------- Mobile: Food delivery app ---------------- */
const FoodScreen = () => (
  <div className="w-full h-full bg-[#FFF9F5] flex flex-col text-[7px] select-none">
    <div className="px-3 pt-4 pb-2">
      <p className="text-[6px] text-gray-400">Deliver to</p>
      <p className="flex items-center gap-1 font-bold text-[#1A1A1A] text-[9px]"><MapPin size={9} className="text-[#C8102E]" /> Orchard Road</p>
      <div className="mt-2 flex items-center gap-1 bg-white rounded-full px-2 py-1.5 shadow-sm">
        <Search size={9} className="text-gray-400" />
        <span className="text-gray-400 text-[6px]">Search restaurants…</span>
      </div>
    </div>
    {/* Categories */}
    <div className="px-3 flex gap-1.5 mb-2">
      {['Bowls', 'Pizza', 'Sushi', 'Vegan'].map((c, i) => (
        <span key={c} className={`rounded-full px-2 py-0.5 text-[6px] font-medium ${i === 0 ? 'bg-[#C8102E] text-white' : 'bg-white text-gray-500'}`}>{c}</span>
      ))}
    </div>
    {/* Promo */}
    <div className="mx-3 mb-2 rounded-xl bg-gradient-to-r from-[#FF7A00] to-[#C8102E] p-2 text-white">
      <p className="font-bold text-[9px]">50% OFF</p>
      <p className="text-[6px] opacity-90">Your first 3 orders</p>
    </div>
    {/* Cards */}
    <div className="px-3 flex-1 space-y-2 overflow-hidden">
      {[
        { img: 'photo-1546069901-ba9599a7e63c', name: 'Fresh Green Bowl', time: '15 min', price: '$12' },
        { img: 'photo-1565299624946-b28f40a0ae38', name: 'Napoli Pizza Co.', time: '25 min', price: '$18' },
      ].map((f) => (
        <div key={f.name} className="bg-white rounded-xl overflow-hidden shadow-sm">
          <img src={`https://images.unsplash.com/${f.img}?w=300&q=70&auto=format&fit=crop`} alt={f.name} className="w-full h-12 object-cover" />
          <div className="p-1.5">
            <p className="font-bold text-[#1A1A1A] text-[8px]">{f.name}</p>
            <div className="flex items-center justify-between text-gray-400 text-[6px] mt-0.5">
              <span className="flex items-center gap-0.5 text-amber-500"><Star size={7} fill="currentColor" /> 4.8</span>
              <span>{f.time}</span>
              <span className="text-[#C8102E] font-semibold">{f.price}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
    {/* Bottom nav */}
    <div className="bg-white px-4 py-2 flex items-center justify-between border-t border-gray-100">
      {[Home, Search, ShoppingBag, User].map((Icon, i) => (
        <Icon key={i} size={11} className={i === 0 ? 'text-[#C8102E]' : 'text-gray-300'} />
      ))}
    </div>
  </div>
);

/* ---------------- Realistic device frames + centered composition ---------------- */
const riseIn = (delay) => ({
  initial: { opacity: 0, y: 60 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 1, delay, ease: [0.16, 1, 0.3, 1] },
});

const DeviceShowcase = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const yLaptop = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const yTablet = useTransform(scrollYProgress, [0, 1], [10, -50]);
  const yPhone = useTransform(scrollYProgress, [0, 1], [40, -60]);

  return (
    <div
      ref={ref}
      className="relative w-full flex items-end justify-center"
      data-testid="device-showcase"
    >
      {/* Stage — centered device family, proportional sizes */}
      <div className="relative origin-bottom scale-[0.52] sm:scale-[0.68] md:scale-90 lg:scale-100 w-[720px] h-[430px]">
        {/* Laptop (MacBook-style) — LARGEST, centered anchor */}
        <motion.div
          {...riseIn(0.05)}
          style={{ y: yLaptop }}
          className="absolute top-0 left-[40%] -translate-x-1/2 z-10 w-[690px]"
          data-testid="device-desktop"
        >
          {/* Floating shadow */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[78%] h-10 bg-[#1A1A1A]/20 blur-2xl rounded-full" />
          <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}>
            {/* Lid */}
            <div className="rounded-t-[16px] bg-[#0d0d0f] p-[7px] pt-2.5 ring-1 ring-white/10 shadow-2xl shadow-black/30">
              <div className="mx-auto mb-1.5 w-1 h-1 rounded-full bg-[#2c2c2e]" />
              <div className="rounded-[7px] overflow-hidden aspect-[16/10] ring-1 ring-black/50">
                <FintechScreen />
              </div>
            </div>
            {/* Base + hinge groove */}
            <div className="relative mx-auto w-[110%] -ml-[5%]">
              <div className="h-[13px] bg-gradient-to-b from-[#d3d3d8] via-[#b4b4b9] to-[#8a8a8f] rounded-b-[12px]" />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-[6px] bg-[#8a8a8f] rounded-b-[7px]" />
            </div>
          </motion.div>
        </motion.div>

        {/* Tablet (iPad-style) — MEDIUM, front-left */}
        <motion.div
          {...riseIn(0.18)}
          style={{ y: yTablet }}
          className="absolute bottom-0 left-[10%] z-20 w-[243px]"
          data-testid="device-tablet"
        >
          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-[72%] h-8 bg-[#1A1A1A]/20 blur-xl rounded-full" />
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 7.5, repeat: Infinity, ease: 'easeInOut' }}>
            <div className="relative rounded-[22px] bg-[#0d0d0f] p-[9px] ring-1 ring-white/10 shadow-2xl shadow-black/35">
              <div className="absolute top-[12px] left-1/2 -translate-x-1/2 w-[3px] h-[3px] rounded-full bg-[#2c2c2e] z-10" />
              <div className="rounded-[15px] overflow-hidden aspect-[3/4] ring-1 ring-black/40">
                <RealEstateScreen />
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Phone (iPhone-style) — SMALLEST, front-right */}
        <motion.div
          {...riseIn(0.3)}
          style={{ y: yPhone }}
          className="absolute bottom-0 right-[22%] z-30 w-[129px]"
          data-testid="device-phone"
        >
          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-[66%] h-8 bg-[#1A1A1A]/25 blur-xl rounded-full" />
          <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}>
            <div className="relative rounded-[28px] bg-[#0d0d0f] p-[5px] ring-1 ring-white/10 shadow-2xl shadow-black/45">
              <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-10 h-[12px] bg-black rounded-full z-10" />
              <div className="rounded-[24px] overflow-hidden aspect-[9/19] ring-1 ring-black/40">
                <FoodScreen />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default DeviceShowcase;
